import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';

import type { AuthenticatedUser } from '@iculogbook/shared';

import { metaRepository } from '../db/repositories/meta.ts';
import { authApi, NetworkError } from './api.ts';
import { clearLocalData } from './localData.ts';

/**
 * `loading` is the state before `/api/auth/me` has answered. Routing must wait
 * for it, so a signed-in user never sees the sign-in screen flash past on a
 * cold start — which on a phone at 3am reads as "it has logged me out".
 */
type Status = 'loading' | 'authenticated' | 'anonymous';

type AuthState = {
  status: Status;
  user: AuthenticatedUser | null;
  register: (input: { email: string; password: string; displayName: string }) => Promise<void>;
  signIn: (input: { email: string; password: string }) => Promise<void>;
  signOut: () => Promise<void>;
};

const AuthContext = createContext<AuthState | null>(null);

export function useAuth(): AuthState {
  const state = useContext(AuthContext);
  if (state === null) throw new Error('useAuth must be used inside an AuthProvider');
  return state;
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<Status>('loading');
  const [user, setUser] = useState<AuthenticatedUser | null>(null);

  useEffect(() => {
    let cancelled = false;

    void authApi
      .me()
      .then(async (resolved) => {
        if (cancelled) return;

        if (resolved === null) {
          // A real 401. The session is gone, so forget the cached identity too.
          await metaRepository.clearCurrentUser();
          setUser(null);
          setStatus('anonymous');
          return;
        }

        await metaRepository.setCurrentUser(resolved);
        setUser(resolved);
        setStatus('authenticated');
      })
      .catch(async (error: unknown) => {
        if (cancelled) return;

        /*
         * The server could not be reached. If this device has signed in before,
         * stay signed in and work from Dexie.
         *
         * This is not a convenience. A trainee opening the app at 3am in a side
         * room with no signal must be able to log — a sign-in wall at that
         * moment loses the entry, which is the failure ARCHITECTURE.md calls
         * out explicitly. Anything other than a network failure is still
         * treated as signed out.
         */
        const cached =
          error instanceof NetworkError ? await metaRepository.getCurrentUser() : undefined;

        if (cancelled) return;
        setUser(cached ?? null);
        setStatus(cached === undefined ? 'anonymous' : 'authenticated');
      });

    return () => {
      cancelled = true;
    };
  }, []);

  const register = useCallback(
    async (input: { email: string; password: string; displayName: string }) => {
      const created = await authApi.register(input);
      await metaRepository.setCurrentUser(created);
      setUser(created);
      setStatus('authenticated');
    },
    [],
  );

  const signIn = useCallback(async (input: { email: string; password: string }) => {
    const signedIn = await authApi.login(input);
    await metaRepository.setCurrentUser(signedIn);
    setUser(signedIn);
    setStatus('authenticated');
  }, []);

  const signOut = useCallback(async () => {
    // Order matters. Destroy the server session first, then wipe this device.
    // If either step fails the user still ends up signed out locally — being
    // stuck signed in is the worse outcome.
    try {
      await authApi.logout();
    } finally {
      await clearLocalData();
      setUser(null);
      setStatus('anonymous');
    }
  }, []);

  const value = useMemo<AuthState>(
    () => ({ status, user, register, signIn, signOut }),
    [status, user, register, signIn, signOut],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
